<!-- 客户登录页 -->
<template>
  <div class="auth-page">
    <Navbar />
    <div class="auth-container">
      <div class="auth-card">
        <!-- 顶部装饰 -->
        <div class="auth-header">
          <div class="auth-logo">
            <div class="logo-icon">
              <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
                <rect width="40" height="40" rx="8" fill="#1a1a1a"/>
                <path d="M12 28L20 12L28 28H24L20 20L16 28H12Z" fill="#d4af37"/>
                <circle cx="20" cy="16" r="2" fill="#d4af37"/>
              </svg>
            </div>
            <div class="logo-text">
              <span class="brand">科曼驰</span>
              <span class="en">KOMANCH</span>
            </div>
          </div>
          <p class="auth-subtitle">登录您的账户</p>
        </div>

        <!-- 登录表单 -->
        <form class="auth-form" @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="username">账号</label>
            <input
              id="username"
              v-model="form.phone"
              type="text"
              placeholder="请输入账号"
              :disabled="loading"
              class="form-input"
              required
            />
          </div>
          <div class="form-group">
            <label for="password">密码</label>
            <div class="password-wrap">
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="请输入密码"
                :disabled="loading"
                class="form-input"
                required
                @keyup.enter="handleSubmit"
              />
              <button type="button" class="toggle-password" @click="showPassword = !showPassword">
                <svg v-if="showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </div>
          </div>

          <button type="submit" class="submit-btn" :disabled="loading">
            <span v-if="loading">登录中...</span>
            <span v-else>登 录</span>
          </button>

          <p class="auth-hint">提交订单前请先登录，如有疑问请联系客服</p>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import Navbar from '@/components/Navbar.vue'

const router = useRouter()
const store = useStore()
const loading = ref(false)
const showPassword = ref(false)

const form = reactive({ phone: '', password: '' })

const handleSubmit = async () => {
  if (!form.phone || !form.password) {
    ElMessage.warning('请输入账号和密码')
    return
  }
  loading.value = true
  try {
    const res = await store.dispatch('login', { phone: form.phone, password: form.password })
    if (res.ActionType === 'OK') {
      ElMessage.success('登录成功')
      router.push('/order-submit')
    } else {
      ElMessage.error(res.errorInfo || '账号或密码错误')
    }
  } catch (e) {
    ElMessage.error('网络错误，请稍后重试')
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
$gold: #d4af37;
$dark: #1a1a1a;
$gray: #6b7280;

.auth-page { min-height: 100vh; background: #f5f7fa; }

.auth-container {
  display: flex; align-items: center; justify-content: center;
  min-height: calc(100vh - 72px); padding: 40px 20px;
}

.auth-card {
  width: 100%; max-width: 400px;
  background: #fff; border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.08);
  padding: 40px 36px;
}

.auth-header { text-align: center; margin-bottom: 32px; }

.auth-logo {
  display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 12px;
  .logo-icon { flex-shrink: 0; }
  .logo-text { display: flex; flex-direction: column; line-height: 1.2;
    .brand { font-size: 22px; font-weight: 700; color: $dark; letter-spacing: 2px; }
    .en    { font-size: 11px; color: $gray; letter-spacing: 3px; }
  }
}

.auth-subtitle { font-size: 14px; color: $gray; margin-top: 4px; }

.auth-form { margin-top: 8px;
  .form-group {
    margin-bottom: 22px;
    label {
      display: block;
      font-size: 14px;
      color: $dark;
      font-weight: 500;
      padding-bottom: 6px;
      line-height: 1.4;
    }
    .form-input {
      width: 100%;
      height: 40px;
      padding: 0 12px;
      font-size: 14px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      transition: all 0.3s ease;
      background: #fff;
      color: $dark;
      box-sizing: border-box;
      &:focus {
        outline: none;
        border-color: $gold;
        box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.1);
      }
      &:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
      }
    }
    .password-wrap {
      position: relative;
      .form-input {
        padding-right: 48px;
      }
      .toggle-password {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        color: #999;
        display: flex;
        align-items: center;
        &:hover {
          color: $dark;
        }
      }
    }
  }
}

.submit-btn {
  width: 100%; height: 44px; border-radius: 8px; font-size: 15px; font-weight: 600;
  background: linear-gradient(135deg, $gold 0%, #b8941f 100%); border: none;
  &:hover { background: linear-gradient(135deg, #e6c040 0%, $gold 100%); }
}

.auth-hint {
  text-align: center; margin-top: 24px; font-size: 12px; color: #bbb;
}
</style>
